import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Card, Modal, Table, message } from 'antd'
import {
  getGlobalTagList,
  createGlobalTag,
  updateGlobalTag,
  deleteGlobalTag,
  mergeGlobalTag
} from '../../modules/customerTag'
import { useDeepEffect } from '../../utils/hooks'
import CustomerTagForm from './components/CustomerTagForm'

/**
 * จัดการป้ายกำกับกลาง (spec 082 — P3)
 *
 * ป้ายกำกับกลางคือ vocabulary เดียวที่ทุกร้านชี้มาที่ record เดียวกัน จึงเป็นสิ่งเดียว
 * ที่เอาไปรวมวิเคราะห์ข้ามร้านได้จริง ส่วนป้ายที่ร้านสร้างเองแม้ชื่อสะกดเหมือนกันเป๊ะ
 * ก็เป็นคนละแถวและรวมกันไม่ได้
 *
 * เนื้อหาต้องจำกัดอยู่ที่ประเภทธุรกิจ / โลจิสติกส์ / วัสดุ เท่านั้น
 * ห้ามเป็นคำตัดสินตัวบุคคล เพราะป้ายระดับกลางเป็นความรับผิดของ G2G เอง (spec D-16)
 */
const CustomerTag = () => {
  const dispatch = useDispatch()
  const [visible, setVisible] = useState(false)
  const [formData, setFormData] = useState({})

  const { globalTagList, isLoading } = useSelector(
    (state) => ({
      globalTagList: state.customerTag.globalTagList,
      isLoading: state.customerTag.isLoading
    }),
    []
  )

  useDeepEffect(() => {
    dispatch(getGlobalTagList())
  }, [])

  const onCreate = () => {
    setFormData({})
    setVisible(true)
  }

  const onEdit = (record) => {
    setFormData(record)
    setVisible(true)
  }

  const onCloseModal = () => {
    setVisible(false)
    setFormData({})
  }

  const onOk = async (data) => {
    const ok =
      formData && formData.id
        ? await dispatch(updateGlobalTag(formData.id, data))
        : await dispatch(createGlobalTag(data))
    if (ok) {
      message.success('บันทึกสำเร็จ')
      onCloseModal()
      await dispatch(getGlobalTagList())
    }
  }

  const onDelete = (record) => {
    Modal.confirm({
      title: `ลบป้ายกำกับกลาง "${record.name}" ?`,
      content:
        'ป้ายจะหายจากรายการที่ทุกร้านเลือกได้ แต่ประวัติว่ารายชื่อไหนเคยถูกติดป้ายนี้ยังคงอยู่',
      okText: 'ลบ',
      okType: 'danger',
      cancelText: 'ยกเลิก',
      onOk: async () => {
        const ok = await dispatch(deleteGlobalTag(record.id))
        if (ok) {
          message.success('ลบสำเร็จ')
          await dispatch(getGlobalTagList())
        }
      }
    })
  }

  const onMerge = (record) => {
    Modal.confirm({
      title: `ยุบป้ายกำกับของร้านเข้า "${record.name}" ?`,
      content:
        'ป้ายกำกับที่ร้านสร้างเองและมีชื่อตรงกันทุกร้าน จะถูกย้ายมาชี้ป้ายกลางอันนี้ ' +
        'แล้วป้ายเดิมของร้านจะถูกลบ ซึ่งคืนโควตาให้ร้านไป 1 ช่อง',
      okText: 'ยุบรวม',
      cancelText: 'ยกเลิก',
      onOk: async () => {
        const result = await dispatch(mergeGlobalTag(record.id))
        if (!result) return
        message.success(
          `ยุบป้ายของร้าน ${result.mergedShopTagCount} อัน · ` +
            `ย้าย ${result.movedMappingCount} รายชื่อ · ` +
            `ทิ้งซ้ำ ${result.droppedDuplicateCount}`
        )
        await dispatch(getGlobalTagList())
      }
    })
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70, align: 'center' },
    { title: 'ชื่อ', dataIndex: 'name', key: 'name', width: 160 },
    { title: 'คำนิยาม', dataIndex: 'description', key: 'description' },
    {
      title: 'สี',
      dataIndex: 'colorHex',
      key: 'colorHex',
      width: 90,
      align: 'center'
    },
    {
      title: '',
      key: 'action',
      width: 240,
      align: 'center',
      render: (_text, record) => (
        <span>
          <Button size="small" onClick={() => onEdit(record)}>
            แก้ไข
          </Button>
          <Button
            size="small"
            style={{ marginLeft: 8 }}
            onClick={() => onMerge(record)}>
            ยุบรวม
          </Button>
          <Button
            size="small"
            type="danger"
            style={{ marginLeft: 8 }}
            onClick={() => onDelete(record)}>
            ลบ
          </Button>
        </span>
      )
    }
  ]

  return (
    <Card
      title="ป้ายกำกับกลาง (Customer Tag)"
      extra={
        <Button type="primary" onClick={onCreate}>
          สร้างป้ายกำกับกลาง
        </Button>
      }>
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={globalTagList}
        pagination={false}
      />
      <CustomerTagForm
        visible={visible}
        formData={formData}
        onOk={onOk}
        onCancel={onCloseModal}
      />
    </Card>
  )
}

export default CustomerTag
